import { useMemo } from 'react';
import { Flame, Calendar, TrendingUp, Award } from 'lucide-react';
import { useStore } from '@/store/useStore';
import { t, getLocalizedString } from '@/i18n/translations';
import { format, startOfWeek, endOfWeek, eachDayOfInterval, parseISO, differenceInDays, max } from 'date-fns';
import { nl, fr, enUS } from 'date-fns/locale';

const DAY_MAP: Record<number, string> = {
  0: 'zo', 1: 'ma', 2: 'di', 3: 'wo', 4: 'do', 5: 'vr', 6: 'za',
};

const DEFAULT_DAYS = ['ma', 'di', 'wo', 'do', 'vr', 'za', 'zo'];

export default function StatsPage() {
  const { settings, progress, templates } = useStore();
  const lang = settings.language;
  const locale = lang === 'nl' ? nl : lang === 'fr' ? fr : enUS;

  const selectedTemplates = useMemo(
    () => templates.filter((t) => (progress.selectedTemplates || []).includes(t.id)),
    [templates, progress.selectedTemplates]
  );

  // Get all habits from selected templates
  const allHabits = useMemo(
    () => selectedTemplates.flatMap((t) => t.habits),
    [selectedTemplates]
  );

  // Helper: get recurring habits scheduled for a specific date
  const getScheduledRecurringHabits = (dateStr: string) => {
    const date = new Date(dateStr + 'T12:00:00');
    const dayCode = DAY_MAP[date.getDay()];
    const customHabits = progress.customHabits || {};

    return allHabits.filter((habit) => {
      const custom = customHabits[habit.id];

      // Skip one-time habits
      const isOneTime = custom?.oneTime ?? habit.oneTime ?? false;
      if (isOneTime) return false;

      // Skip disabled habits
      if (custom?.disabled) return false;

      // Check if scheduled for this day
      const effectiveDays = custom?.days || habit.days || DEFAULT_DAYS;
      return effectiveDays.includes(dayCode);
    });
  };

  // Calculate weekly completion rate with all statuses
  const weeklyStats = useMemo(() => {
    const today = new Date();
    const weekStart = startOfWeek(today, { weekStartsOn: 1 });
    const weekEnd = endOfWeek(today, { weekStartsOn: 1 });
    const days = eachDayOfInterval({ start: weekStart, end: weekEnd });

    // Only show days from start date onwards
    const startDate = progress.startDate ? parseISO(progress.startDate) : null;

    return days.map((day) => {
      const dateStr = format(day, 'yyyy-MM-dd');
      const scheduled = getScheduledRecurringHabits(dateStr);

      // Count completed recurring habits for this day
      const completedHabitIds = new Set(
        progress.completions
          .filter((c) => c.date === dateStr && c.completed)
          .map((c) => c.habitId)
      );

      // Count skipped habits for this day
      const skippedHabitIds = new Set(
        Object.entries(progress.skippedHabits || {})
          .filter(([_, dates]) => dates.includes(dateStr))
          .map(([habitId]) => habitId)
      );

      const completedCount = scheduled.filter((h) => completedHabitIds.has(h.id)).length;
      const skippedCount = scheduled.filter((h) => skippedHabitIds.has(h.id)).length;
      const missedCount = scheduled.length - completedCount - skippedCount;

      // Check if day is before start date or in the future
      const isBeforeStart = startDate && day < startDate;
      const isFuture = day > today;

      return {
        date: day,
        dateStr,
        scheduled: scheduled.length,
        completed: completedCount,
        skipped: skippedCount,
        missed: isFuture ? 0 : missedCount,
        completionRate: scheduled.length > 0 ? completedCount / scheduled.length : 0,
        dayName: format(day, 'EEE', { locale }),
        isBeforeStart,
        isFuture,
      };
    });
  }, [progress.completions, progress.skippedHabits, progress.startDate, progress.customHabits, allHabits, locale]);

  // Calculate total stats (only recurring habits)
  const totalStats = useMemo(() => {
    const customHabits = progress.customHabits || {};
    // Get IDs of recurring habits
    const recurringHabitIds = new Set(
      allHabits
        .filter((habit) => {
          const custom = customHabits[habit.id];
          const isOneTime = custom?.oneTime ?? habit.oneTime ?? false;
          return !isOneTime;
        })
        .map((h) => h.id)
    );

    // Count only recurring habit completions
    const totalCompletions = progress.completions.filter(
      (c) => c.completed && recurringHabitIds.has(c.habitId)
    ).length;

    // Count days with at least one recurring completion
    const uniqueDays = new Set(
      progress.completions
        .filter((c) => c.completed && recurringHabitIds.has(c.habitId))
        .map((c) => c.date)
    ).size;

    const daysSinceStart = progress.startDate
      ? differenceInDays(new Date(), parseISO(progress.startDate)) + 1
      : 0;

    return {
      totalCompletions,
      uniqueDays,
      daysSinceStart,
      streak: progress.streak,
    };
  }, [progress, allHabits]);

  return (
    <div className="min-h-screen flex flex-col pb-20">
      {/* Content */}
      <div className="flex-1 px-4 py-6 space-y-6">
        {/* Summary Cards */}
        <div className="grid grid-cols-2 gap-3">
          <div className="bg-white/80 backdrop-blur-sm rounded-xl p-4 shadow-sm">
            <div className="flex items-center gap-2 mb-2">
              <Flame className="w-5 h-5 text-orange-500" />
              <span className="text-sm text-gray-600">
                {lang === 'nl' ? 'Huidige streak' : lang === 'fr' ? 'Série actuelle' : 'Current streak'}
              </span>
            </div>
            <span className="text-2xl font-bold text-gray-900">{totalStats.streak}</span>
            <span className="text-sm text-gray-500 ml-1">
              {lang === 'nl' ? 'dagen' : lang === 'fr' ? 'jours' : 'days'}
            </span>
          </div>

          <div className="bg-white/80 backdrop-blur-sm rounded-xl p-4 shadow-sm">
            <div className="flex items-center gap-2 mb-2">
              <Award className="w-5 h-5 text-primary-500" />
              <span className="text-sm text-gray-600">
                {lang === 'nl' ? 'Totaal voltooid' : lang === 'fr' ? 'Total terminé' : 'Total completed'}
              </span>
            </div>
            <span className="text-2xl font-bold text-gray-900">{totalStats.totalCompletions}</span>
            <span className="text-sm text-gray-500 ml-1">
              {lang === 'nl' ? 'gewoontes' : lang === 'fr' ? 'habitudes' : 'habits'}
            </span>
          </div>

          <div className="bg-white/80 backdrop-blur-sm rounded-xl p-4 shadow-sm">
            <div className="flex items-center gap-2 mb-2">
              <Calendar className="w-5 h-5 text-emerald-500" />
              <span className="text-sm text-gray-600">
                {lang === 'nl' ? 'Actieve dagen' : lang === 'fr' ? 'Jours actifs' : 'Active days'}
              </span>
            </div>
            <span className="text-2xl font-bold text-gray-900">{totalStats.uniqueDays}</span>
            <span className="text-sm text-gray-500 ml-1">
              / {totalStats.daysSinceStart}
            </span>
          </div>

          <div className="bg-white/80 backdrop-blur-sm rounded-xl p-4 shadow-sm">
            <div className="flex items-center gap-2 mb-2">
              <span className="text-lg">📋</span>
              <span className="text-sm text-gray-600">
                {lang === 'nl' ? 'Actieve programma\'s' : lang === 'fr' ? 'Programmes actifs' : 'Active programs'}
              </span>
            </div>
            <span className="text-2xl font-bold text-gray-900">{selectedTemplates.length}</span>
          </div>
        </div>

        {/* Weekly Chart */}
        <div className="bg-white/80 backdrop-blur-sm rounded-xl p-4 shadow-sm">
          <h3 className="font-semibold text-gray-800 mb-4">
            {lang === 'nl' ? 'Deze week' : lang === 'fr' ? 'Cette semaine' : 'This week'}
          </h3>
          <div className="flex items-end justify-between gap-2 h-32">
            {weeklyStats.map((day) => {
              const isToday = day.dateStr === format(new Date(), 'yyyy-MM-dd');
              const total = day.scheduled || 1;

              // Calculate percentages for stacked bar
              const completedPct = (day.completed / total) * 100;
              const skippedPct = (day.skipped / total) * 100;
              const missedPct = (day.missed / total) * 100;

              // Show empty state for before start or future
              if (day.isBeforeStart || day.isFuture || day.scheduled === 0) {
                return (
                  <div key={day.dateStr} className="flex-1 flex flex-col items-center gap-1">
                    <span className="text-xs text-gray-400">-</span>
                    <div
                      className="w-full rounded-t-lg bg-gray-100"
                      style={{ height: '4px' }}
                    />
                    <span className={`text-xs ${isToday ? 'font-semibold text-primary-600' : 'text-gray-400'}`}>
                      {day.dayName}
                    </span>
                  </div>
                );
              }

              return (
                <div key={day.dateStr} className="flex-1 flex flex-col items-center gap-1">
                  <span className="text-xs text-gray-500">
                    {day.completed}/{day.scheduled}
                  </span>
                  {/* Stacked bar */}
                  <div className="w-full flex flex-col-reverse" style={{ height: '100%' }}>
                    {/* Completed (green) - bottom */}
                    {completedPct > 0 && (
                      <div
                        className={`w-full ${isToday ? 'bg-emerald-500' : 'bg-emerald-400'} first:rounded-b-lg last:rounded-t-lg`}
                        style={{ height: `${completedPct}%`, minHeight: completedPct > 0 ? '4px' : '0' }}
                      />
                    )}
                    {/* Skipped (gray) - middle */}
                    {skippedPct > 0 && (
                      <div
                        className={`w-full ${isToday ? 'bg-gray-400' : 'bg-gray-300'} first:rounded-b-lg last:rounded-t-lg`}
                        style={{ height: `${skippedPct}%`, minHeight: skippedPct > 0 ? '4px' : '0' }}
                      />
                    )}
                    {/* Missed (red) - top */}
                    {missedPct > 0 && (
                      <div
                        className={`w-full ${isToday ? 'bg-red-400' : 'bg-red-300'} first:rounded-b-lg last:rounded-t-lg`}
                        style={{ height: `${missedPct}%`, minHeight: missedPct > 0 ? '4px' : '0' }}
                      />
                    )}
                    {/* Empty state if nothing */}
                    {completedPct === 0 && skippedPct === 0 && missedPct === 0 && (
                      <div className="w-full bg-gray-200 rounded-lg" style={{ height: '4px' }} />
                    )}
                  </div>
                  <span className={`text-xs ${isToday ? 'font-semibold text-primary-600' : 'text-gray-500'}`}>
                    {day.dayName}
                  </span>
                </div>
              );
            })}
          </div>
          <div className="flex items-center justify-center gap-4 mt-3 text-xs text-gray-500">
            <span className="flex items-center gap-1">
              <span className="w-3 h-3 rounded bg-emerald-400"></span>
              {lang === 'nl' ? 'Voltooid' : lang === 'fr' ? 'Terminé' : 'Done'}
            </span>
            <span className="flex items-center gap-1">
              <span className="w-3 h-3 rounded bg-gray-300"></span>
              {lang === 'nl' ? 'Overgeslagen' : lang === 'fr' ? 'Passé' : 'Skipped'}
            </span>
            <span className="flex items-center gap-1">
              <span className="w-3 h-3 rounded bg-red-300"></span>
              {lang === 'nl' ? 'Gemist' : lang === 'fr' ? 'Manqué' : 'Missed'}
            </span>
          </div>
        </div>

        {/* Active Programs */}
        <div className="bg-white/80 backdrop-blur-sm rounded-xl p-4 shadow-sm">
          <h3 className="font-semibold text-gray-800 mb-3">
            {lang === 'nl' ? 'Actieve gewoontes' : lang === 'fr' ? 'Habitudes actives' : 'Active habits'}
          </h3>
          <div className="space-y-2">
            {selectedTemplates.map((template) => (
              <div key={template.id} className="flex items-center gap-3 p-2 rounded-lg bg-gray-50">
                <span className="text-xl">{template.icon}</span>
                <div className="flex-1">
                  <span className="font-medium text-gray-800">
                    {getLocalizedString(template.name, lang)}
                  </span>
                  <p className="text-xs text-gray-500">
                    {template.habits.length} {lang === 'nl' ? 'gewoontes' : lang === 'fr' ? 'habitudes' : 'habits'}
                  </p>
                </div>
              </div>
            ))}
          </div>
        </div>
      </div>
    </div>
  );
}
